import { RootState } from '@/store/rootState';
import { Farm } from '@/core/interfaces/farm';
import { Vehicle } from '@/core/interfaces/vehicle';
import { VehicleType } from '@/core/interfaces/vehicleType';
import { User } from '@/core/interfaces/user';
import { Role } from '@/core/interfaces/role';
import { NotificationType } from '@/core/interfaces/notificationType';
import { VehiclePhase } from '@/core/interfaces/vehiclePhase';
import { Phone } from '@/core/interfaces/phone';
import NotificationLevel, { AllNotificationLevels } from '@/core/enums/notificationLevel';
import { FarmGroup } from '@/core/interfaces/farmGroup';

const colors = [
    '#73bd07',
    '#1F78B4',
    '#FD3535',
    '#766E68',
    '#FFC700',
    '#C56A28',
    '#4D33DC',
];

let colorIndex = 0;
const roleColors: Record<number, string> = {};

export const farmsSet = (state: RootState): Farm[] =>
    state.farmsList.map((id: number) => state.farms[id]).sort((a, b) =>
        a.name.localeCompare(b.name));

export const farmsByVehicleType = (state: RootState, getters: any) => (vehicleTypeId: number): Farm[] =>
    state.farmVehicleTypes
        .filter((farmVehicleType) => farmVehicleType.vehicle_type_id === vehicleTypeId)
        .map((farmVehicleType) => getters.farmById(farmVehicleType.farm_id))
        .filter((farm: Farm) => !!farm);

export const farmGroups = (state: RootState): FarmGroup[] => state
    .farmGroups.sort((a, b) => a.name.localeCompare(b.name));

export const farmGroupById = (state: RootState) => (id: number): FarmGroup | undefined =>
    state.farmGroups.find((group) => group.id === id);

export const vehicleTypesByFarm = (state: RootState, getters: any) => (farmId: number): VehicleType[] =>
    state.farmVehicleTypes
        .filter((farmVehicleType) => farmVehicleType.farm_id === farmId)
        .map((farmVehicleType) => getters.vehicleTypeById(farmVehicleType.vehicle_type_id));

export const vehiclesSet = (state: RootState): Vehicle[] =>
    state.vehiclesList.map((id: number) => state.vehicles[id]).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }));

export const vehicleTypesSet = (state: RootState): VehicleType[] =>
    state.vehicleTypesList.map((id: number) => state.vehicleTypes[id])
        .sort((a, b) =>
            a.name.localeCompare(b.name));

export const usersSet = (state: RootState): User[] =>
    state.usersList.map((id: number) => state.users[id])
        .sort((a, b) =>
            a.username.localeCompare(b.username));

export const rolesSet = (state: RootState): Role[] =>
    state.rolesList.map((id: number) => state.roles[id])
        .sort((a, b) =>
            a.code_name.localeCompare(b.code_name));

export const notifyableRolesSet = (state: RootState, getters: any): Role[] =>
    getters.rolesSet.filter((role: Role) => role.notifyable);

export const assignableRolesSet = (state: RootState, getters: any): Role[] =>
    getters.rolesSet.filter((role: Role) => role.assignable);

export const notificationTypesSet = (state: RootState): NotificationType[] =>
    state.notificationTypesList.map((id: number) => state.notificationTypes[id])
        .sort((a, b) => a.code - b.code);

export const vehiclePhasesSet = (state: RootState): VehiclePhase[] =>
    state.vehiclePhasesList.map((id: number) => state.vehiclePhases[id]);

export const vehiclePhasesByVehicleTypeId = (state: RootState) => (vehicleTypeId: number): VehiclePhase[] =>
    state.vehiclePhasesList
        .map((id: number) => state.vehiclePhases[id])
        .filter((phase: VehiclePhase) => phase.vehicle_type_id === vehicleTypeId);

export const currentUser = (state: RootState): User | null => state.currentUser;

export const currentUserId = (state: RootState): number | null => state.currentUser?.id || null;

export const isAdmin = (state: RootState, getters: any): boolean => getters.currentUser?.admin;

export const userById = (state: RootState) => (id: number): User => state.users[id];

export const farmById = (state: RootState) => (id: number): Farm => state.farms[id];

export const vehiclePhaseById = (state: RootState) => (id: number): VehiclePhase => state.vehiclePhases[id];

export const vehicleById = (state: RootState) => (id: number): Vehicle => state.vehicles[id];

export const vehicleByName = (state: RootState, getters: any) => (name: string): Vehicle => getters.vehiclesSet.find((vehicle: Vehicle) => vehicle.name === name);

export const vehiclesByFarm = (state: RootState, getters: any) => (farmId: number): Vehicle[] => getters.vehiclesSet
    .filter((vehicle: Vehicle) => vehicle.farm_id === farmId);

export const vehicleTypeById = (state: RootState) => (id: number): VehicleType => state.vehicleTypes[id];

export const vehicleTypeByName = (state: RootState) => (name: string): VehicleType => Object.values(state.vehicleTypes).find(type => type.name === name) as VehicleType;

export const notificationTypeById = (state: RootState) => (id: number): NotificationType => state.notificationTypes[id];

export const notificationLevelByTypeId = (state: RootState) => (id: number): NotificationLevel => AllNotificationLevels[state.notificationTypes[id].level] || NotificationLevel.INFO;

export const notificationTypeByCode = (state: RootState) => (code: number, vehicleTypeId: number): NotificationType => Object.values(state.notificationTypes)
    .find(type => type.code === code && type.vehicle_type_id === vehicleTypeId) as NotificationType;

export const notificationTypesByVehicleTypeId = (state: RootState) => (vehicleTypeId: number): NotificationType[] => Object.values(state.notificationTypes)
    .filter(type => type.vehicle_type_id === vehicleTypeId)
    .sort((a, b) => a.code - b.code);

export const roleById = (state: RootState) => (id: number): Role => state.roles[id];

export const assignableRoles = (state: RootState, getters: any): Role[] => getters.rolesSet
    .filter((role: Role) => role.assignable);

export const roleByName = (state: RootState, getters: any) => (name: string): Role => getters.rolesSet
    .find((role: Role) => role.code_name === name);

export const usersByRoleId = (state: RootState) => (roleId: number): User[] => state.userRoles
    .filter(userRole => userRole.role_id === roleId)
    .filter(userRole => state.usersList.includes(userRole.user_id))
    .map(userRole => state.users[userRole.user_id])
    .sort((a, b) => a.username.localeCompare(b.username));

export const roleColorById = (state: RootState) => (roleId: number): string => {
    if (Object.keys(roleColors).includes(String(roleId))) {
        return roleColors[roleId];
    }

    roleColors[roleId] = colors[colorIndex];
    colorIndex++;

    return roleColors[roleId];
};

export const localeKeys = (state: RootState): string[] => Object.keys(state.locales);

export const phonesSet = (state: RootState, getters: any): Phone[] =>
    state.phones
        .sort((a, b) =>
            getters.userById(a.user_id)?.username.localeCompare(getters.userById(b.user_id)?.username));

export const phoneById = (state: RootState) => (phoneId: number): Phone =>
    state.phones.find(phone => phone.id === phoneId) as Phone;

export const phoneByUdid = (state: RootState) => (udid: string): Phone =>
    state.phones.find(phone => phone.phone_udid === udid) as Phone;

export const visibleVehicleTypeIds = (state: RootState): number[] => state.visibleVehicleTypeIds;

export const visibleVehicleTypes = (state: RootState, getters: any): VehicleType[] =>
    state.visibleVehicleTypeIds.map(id => getters.vehicleTypeById(id));

export const resolvedRegistrationPossibleActionsByVehicleTypeId = (state: RootState) => (vehicleTypeId: number): string[] => state
    .resolvedRegistrationData.find(item => item.vehicle_type_id === vehicleTypeId)?.possible_actions.sort((a, b) => a.localeCompare(b)) || [];

export const resolvedRegistrationPossibleCausesByVehicleTypeId = (state: RootState) => (vehicleTypeId: number): string[] => state
    .resolvedRegistrationData.find(item => item.vehicle_type_id === vehicleTypeId)?.possible_causes.sort((a, b) => a.localeCompare(b)) || [];

export const hasRole = (state: RootState) => (role: string): boolean => state.currentUser?.roles.includes(role) ?? false;
export const isInternalUser = (state: RootState, getters: any) => ['admin', 'developer', 'operator'].some(role => getters.hasRole(role));
export const isDeveloper = (state: RootState, getters: any) => getters.hasRole('developer');
